import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DefaultLayout from '../../layout/DefaultLayout';
import Loading from '../../components/Loading';
import Alert from '../../components/Alert';
import beneficiarioService from '../../services/beneficiarioService';
import { useGlobalState } from '../../state';
import { FaDownload, FaFileAlt, FaShareAlt } from 'react-icons/fa';
import { toCurrency } from '../../utils/formaters';

type ReciboView = {
  id?: string;
  mes?: string;
  anio?: string;
  mesLetra?: string;
  beneficiario?: string;
  tipoBeneficio?: string;
  numeroRecibo?: string;
  archivo?: string;
};

const mesesNombre = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'
];

const DetalleRecibo: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user] = useGlobalState('user');

  const query = new URLSearchParams(location.search);
  const mes = query.get('mes') ?? '';
  const anio = query.get('anio') ?? '';
  const numero = query.get('numero') ?? '';

  const [loading, setLoading] = useState<boolean>(true);
  const [recibo, setRecibo] = useState<ReciboView | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    const buildRecibo = () => {
      if (!user || !mes || !anio) {
        setRecibo(null);
        setLoading(false);
        return;
      }

      const mesIndex = Math.max(0, Math.min(11, parseInt(mes, 10) - 1));
      const mesLetra = mesesNombre[mesIndex] ?? mes;

      const periodoSinGuion = `${anio}${mes}`;
        const numeroRecibo = numero || `${periodoSinGuion}${user.datosUsuario.legajoPrevisional}/${user.datosUsuario.beneficiario?.tipoBeneficiarioId ?? ''}`;

      const r: ReciboView = {
        id: undefined,
        mes,
        anio,
        mesLetra,
          beneficiario: `${user.datosUsuario.beneficiario?.apellido ?? ''} ${user.datosUsuario.beneficiario?.nombre ?? ''}`.trim(),
          tipoBeneficio: user.datosUsuario.beneficiario?.tipoBeneficio ?? user.datosUsuario.beneficiario?.tipoBeneficiario ?? '—',
        numeroRecibo,
        archivo: `Recibo_${mesLetra}_${anio}.pdf`,
      };
      setRecibo(r);
      setLoading(false);
    };

    buildRecibo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, mes, anio, numero]);

  const handleDescargar = async () => {
    if (!user || !recibo) return;
    setError(undefined);
    setLoading(true);
    try {
      const IdAfiliado = parseInt(user.datosUsuario.legajoPrevisional, 10);
        const IdTipoBeneficiario = user.datosUsuario.beneficiario?.tipoBeneficiarioId ?? user.datosUsuario.beneficiario?.tipoBeneficiario ?? 0;
      const periodo = `${recibo.anio}${recibo.mes}`;

      // Prefer web endpoint that returns base64
      const resWeb: any = await beneficiarioService.getReciboAfiliadoWeb?.(IdAfiliado, IdTipoBeneficiario, periodo);
      const base64Pdf = resWeb?.data ?? resWeb?.Data ?? resWeb?.data?.reporte ?? null;

      if (!base64Pdf) {
        throw new Error('No se encontró el PDF para este recibo.');
      }

      // Decode base64 and download
      const byteCharacters = atob(base64Pdf);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      const fileName = `Recibo_${recibo.mesLetra}_${recibo.anio}.pdf`;
      const url = window.URL.createObjectURL(blob);

      // Try open in new tab, otherwise force download
      const newWindow = window.open(url, '_blank');
      if (!newWindow) {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }

      window.URL.revokeObjectURL(url);
    } catch (ex: any) {
      console.error('Error al descargar recibo:', ex);
      setError(ex?.message ?? 'No se pudo descargar el recibo.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DefaultLayout>
        <div className="p-6">
          <Loading statusText="Cargando recibo..." />
        </div>
      </DefaultLayout>
    );
  }

  if (!recibo) {
    return (
      <DefaultLayout>
        <div className="p-6">
          <Alert type="failure" message={error ?? 'Recibo no encontrado'} />
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <div className="p-6">
              <div className="max-w-6xl mx-auto bg-white dark:bg-boxdark p-6 rounded-xl">

                  {error && <Alert type="failure" message={error} />}

          <div className="flex items-center gap-3 mb-4">
            <FaFileAlt className="text-2xl text-gray-700" />
            <h1 className="text-xl font-semibold">Recibo de Haberes – {recibo.mesLetra} {recibo.anio}</h1>
          </div>          

          <div className="bg-gray-50 p-4 rounded mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="text-sm text-gray-600">Beneficiario</div>
              <div className="text-sm font-semibold text-right">{recibo.beneficiario}</div>

              <div className="text-sm text-gray-600">Tipo de beneficio</div>
              <div className="text-sm font-semibold text-right">{recibo.tipoBeneficio}</div>

              <div className="text-sm text-gray-600">Número de recibo</div>
              <div className="text-sm font-semibold text-right">{recibo.numeroRecibo}</div>

              <div className="text-sm text-gray-600">Período</div>
              <div className="text-sm font-semibold text-right">{recibo.mes}-{recibo.anio}</div>

              <div className="text-sm text-gray-600">Archivo</div>
              <div className="text-sm font-semibold text-right">{recibo.archivo}</div>
            </div>
          </div>

          <div className="flex gap-3 justify-center">
            <button
              onClick={handleDescargar}
              className="inline-flex items-center gap-2 bg-primario-celeste-800 text-white px-4 py-2 rounded hover:bg-primario-azul-900"
            >
              <FaDownload /> Descargar
            </button>

            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded"
            >
              Volver
            </button>
          </div>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default DetalleRecibo;